
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { CertificatePrintPreview } from "./CertificatePrintPreview";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getPreviewUrl } from "@/utils/imageUtils";
import { Badge } from "@/components/ui/badge";
import { TestTube2, Palette, FileText } from "lucide-react";

interface PrintDetailsProps {
  print: any;
  onIssueCertificate: () => void;
  onOpenChange: (open: boolean) => void;
}

export function PrintDetails({ print, onIssueCertificate, onOpenChange }: PrintDetailsProps) {
  const [showCertificatePreview, setShowCertificatePreview] = useState(false);
  const [isIssuingCertificate, setIsIssuingCertificate] = useState(false);
  const queryClient = useQueryClient();
  
  console.log('Print details:', print);
  
  const isTestPrint = print.caption?.toLowerCase().includes('test print');
  const isSamplePrint = print.caption?.toLowerCase().includes('sample print');
  const isStandardPrint = !isTestPrint && !isSamplePrint;
  
  // Get artwork information - handle both standard prints and test/sample prints
  const getArtworkInfo = () => {
    // Standard print with edition and artwork
    if (print.editions?.artworks) {
      return {
        title: print.editions.artworks.title,
        creator: print.editions.artworks.creators,
        preview: print.editions.artworks.preview,
        creation_date: print.editions.artworks.creation_date,
        art_technique: print.editions.artworks.art_technique,
        width: print.editions.width,
        height: print.editions.height,
        top_margin: print.editions.top_margin,
        bottom_margin: print.editions.bottom_margin,
        left_margin: print.editions.left_margin,
        right_margin: print.editions.right_margin,
        edition_name: print.editions.edition_name,
        edition_limit: print.editions.edition_limit
      };
    } 
    // Special print (test/sample) with artwork info via highres_file
    else if (print.highres_file?.artworks && print.highres_file.artworks[0]) {
      const artwork = print.highres_file.artworks[0];
      return {
        title: artwork.title,
        creator: artwork.creators,
        preview: artwork.preview,
        creation_date: artwork.creation_date,
        art_technique: artwork.art_technique,
        width: print.width,
        height: print.height,
        top_margin: print.top_margin,
        bottom_margin: print.bottom_margin,
        left_margin: print.left_margin,
        right_margin: print.right_margin,
        edition_name: null,
        edition_limit: null
      };
    }
    // Fallback for prints with no artwork information
    else {
      return {
        title: print.caption || 'Untitled Print',
        creator: null,
        preview: null,
        creation_date: null,
        art_technique: null,
        width: print.width,
        height: print.height,
        top_margin: print.top_margin,
        bottom_margin: print.bottom_margin,
        left_margin: print.left_margin,
        right_margin: print.right_margin,
        edition_name: null,
        edition_limit: null
      };
    }
  };

  const artworkInfo = getArtworkInfo();
  
  // Show Issue Certificate button only if print is waiting to print, has no certificate, and requires certification
  // Also check if we're not currently issuing a certificate
  const showIssueCertificateButton = 
    print.status === 'waiting_to_print' && 
    !print.certificate_id && 
    print.certified &&
    !isIssuingCertificate;
  
  // Show Print Certificate button if the print has a certificate
  const showPrintCertificateButton = !!print.certificate_id;
  
  const showMarkAsPrintedButton = print.status === 'waiting_to_print' && !print.certified;

  const handleMarkAsPrinted = async () => {
    try {
      const { error } = await supabase
        .from('prints')
        .update({ status: 'printed' })
        .eq('print_id', print.print_id);

      if (error) throw error;

      toast.success('Print marked as printed successfully');
      queryClient.invalidateQueries({ queryKey: ["prints-list"] });
      onOpenChange(false); // Close the popup after successful update
    } catch (error) {
      console.error('Error marking print as printed:', error);
      toast.error('Failed to mark print as printed');
    }
  };

  const handlePrintCertificate = () => {
    console.log('Opening certificate preview for print:', print);
    setShowCertificatePreview(true);
  };

  const handleIssueCertificate = () => {
    setIsIssuingCertificate(true);
    onIssueCertificate();
  };

  const getPrintTypeDisplay = () => {
    if (isTestPrint) {
      return (
        <div className="flex items-center space-x-2 mb-3">
          <TestTube2 size={18} className="text-purple-500" />
          <Badge variant="outline" className="border-purple-500 text-purple-700">Test Print</Badge>
        </div>
      );
    } else if (isSamplePrint) {
      return (
        <div className="flex items-center space-x-2 mb-3">
          <Palette size={18} className="text-blue-500" />
          <Badge variant="outline" className="border-blue-500 text-blue-700">Sample Print</Badge>
        </div>
      );
    } else {
      return (
        <div className="flex items-center space-x-2 mb-3">
          <FileText size={18} className="text-green-600" />
          <Badge variant="outline" className="border-green-500 text-green-700">Standard Print</Badge>
        </div>
      );
    }
  };

  return (
    <>
      <div className="grid grid-cols-[200px_1fr_1fr] gap-4 py-4">
        <div className="col-span-1">
          {artworkInfo.preview && (
            <img 
              src={getPreviewUrl(artworkInfo.preview)} 
              alt={artworkInfo.title}
              className="w-full h-auto rounded-lg shadow-md"
            />
          )}
        </div>

        <div>
          {getPrintTypeDisplay()}
          
          <h3 className="font-medium mb-2">Artwork Information</h3>
          <p className="text-sm mb-1">
            <span className="font-medium">Title:</span> {artworkInfo.title}
          </p>
          {artworkInfo.creator && (
            <p className="text-sm mb-1">
              <span className="font-medium">Artist:</span> {artworkInfo.creator.name} {artworkInfo.creator.surname || ""}
              {artworkInfo.creator.nick_name && ` (${artworkInfo.creator.nick_name})`}
            </p>
          )}
          {artworkInfo.creation_date && (
            <p className="text-sm mb-1">
              <span className="font-medium">Creation Date:</span> {artworkInfo.creation_date}
            </p>
          )}
          {artworkInfo.art_technique && (
            <p className="text-sm mb-1">
              <span className="font-medium">Technique:</span> {artworkInfo.art_technique}
            </p>
          )}
          {artworkInfo.edition_name && (
            <p className="text-sm mb-1">
              <span className="font-medium">Edition:</span> {artworkInfo.edition_name}
            </p>
          )}
          {artworkInfo.width && artworkInfo.height && (
            <p className="text-sm mb-1">
              <span className="font-medium">Size:</span> {artworkInfo.width} x {artworkInfo.height} cm
            </p>
          )}
          
          {artworkInfo.top_margin !== null && (
            <div className="mt-4">
              <h4 className="font-medium mb-2">Margins</h4>
              <p className="text-sm mb-1">
                <span className="font-medium">Top:</span> {artworkInfo.top_margin || 0} cm
              </p>
              <p className="text-sm mb-1">
                <span className="font-medium">Bottom:</span> {artworkInfo.bottom_margin || 0} cm
              </p>
              <p className="text-sm mb-1">
                <span className="font-medium">Left:</span> {artworkInfo.left_margin || 0} cm
              </p>
              <p className="text-sm mb-1">
                <span className="font-medium">Right:</span> {artworkInfo.right_margin || 0} cm
              </p>
            </div>
          )}
        </div>
        
        <div>
          <h3 className="font-medium mb-2">Print Information</h3>
          {print.edition_number && (
            <p className="text-sm mb-1">
              <span className="font-medium">Print Number:</span> #{print.edition_number}
              {artworkInfo.edition_limit && `/${artworkInfo.edition_limit}`}
            </p>
          )}
          <p className="text-sm mb-1">
            <span className="font-medium">Status:</span>{" "}
            <span className="capitalize">{print.status?.replace(/_/g, ' ')}</span>
          </p>
          <p className="text-sm mb-1">
            <span className="font-medium">Media:</span> {print.media?.media_name || "Not specified"}
          </p>
          <p className="text-sm mb-1">
            <span className="font-medium">Print Provider:</span> {print.print_service_providers?.name || "Not specified"}
          </p>
          <p className="text-sm mb-1">
            <span className="font-medium">Certification:</span>{" "}
            {print.certified ? (
              print.certificates?.hologram_number || "CERTIFIED"
            ) : (
              "Not Required"
            )}
          </p>
          <p className="text-sm mb-1">
            <span className="font-medium">Print Date:</span>{" "}
            {print.print_date ? new Date(print.print_date).toLocaleDateString() : "Not specified"}
          </p>

          {showIssueCertificateButton && (
            <Button 
              className="mt-4"
              onClick={handleIssueCertificate}
            >
              Issue Certificate
            </Button>
          )}

          {showPrintCertificateButton && (
            <Button 
              className="mt-4"
              onClick={handlePrintCertificate}
            >
              Print Certificate
            </Button>
          )}

          {showMarkAsPrintedButton && (
            <Button 
              className="mt-4"
              onClick={handleMarkAsPrinted}
            >
              Mark as Printed
            </Button>
          )}
        </div>
      </div>

      {showCertificatePreview && print.certificates && (
        <CertificatePrintPreview
          print={print}
          certificate={print.certificates}
          open={showCertificatePreview}
          onOpenChange={setShowCertificatePreview}
        />
      )}
    </>
  );
}
